import React, { memo, useEffect, useState } from "react";
import { Breadcrumbs, Header, Workspace } from "components/Shared";
import { useParams } from "react-router-dom";
import axios from "v2/utils/axios";
import BackToPortfolioLink from "./components/BackToPortfolioLink";
import DateRangeFilter from "./components/DateRangeFilter";
import SlaMetricCards from "./components/SlaMetricCards";
import TaskLifecycleFilter from "./components/TaskLifecycleFilter";
import TasksRequiringAttention from "./components/TasksRequiringAttention";
import TeamMemberFilter from "./components/TeamMemberFilter";
import { useSlaDashboard } from "./hooks/useSlaDashboard";
import { DateRangePreset, SlaActiveFilters, TaskLifecycleStatus } from "./types";

// slaStatus removed — per-section status is owned inside TasksRequiringAttention
// (api-contract D0.5 / react-frontend-tasks F4/F5).
const DEFAULT_FILTERS: SlaActiveFilters = {
  dateRangePreset: "this_financial_year",
  startDate: null,
  endDate: null,
  teamMemberIds: [],
  taskLifecycleStatus: "open", // api-contract D0.6 — default lifecycle is "open"
};

const SlaMonitorProperty = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const [filters, setFilters] = useState<SlaActiveFilters>(DEFAULT_FILTERS);
  const [propertyName, setPropertyName] = useState<string>("");
  const { summary, isLoading, unauthorized } = useSlaDashboard(filters, propertyId);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    // Reset so the heading shows "Loading…" immediately when propertyId changes
    // (prevents the stale name from the previous property lingering until the
    // new fetch resolves — S2).
    setPropertyName("");
    // Use a bare relative path so the shared axios instance's baseURL is
    // applied exactly once (B1 — do NOT prepend REACT_APP_API_ENDPOINT_V2
    // here; the baseURL on the axiosServices instance already contains it).
    axios
      .get(`properties/${propertyId}`)
      .then((res) => {
        if (cancelled) return;
        const name = res.data?.data?.attributes?.name ?? "";
        setPropertyName(name);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error fetching property:", err);
        // Leave propertyName empty — falls back to "Property SLA" in breadcrumb
        // and "Loading…" in the heading.
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const handlePresetChange = (preset: DateRangePreset) => {
    setFilters((f) => ({ ...f, dateRangePreset: preset, startDate: null, endDate: null }));
  };

  const handleCustomRange = (start: string, end: string) => {
    setFilters((f) => ({ ...f, dateRangePreset: null, startDate: start, endDate: end }));
  };

  const handleTeamMembersChange = (ids: string[]) => {
    setFilters((f) => ({ ...f, teamMemberIds: ids }));
  };

  const handleLifecycleChange = (status: TaskLifecycleStatus) => {
    setFilters((f) => ({ ...f, taskLifecycleStatus: status }));
  };

  // When the backend returns 401 (feature flag disabled), render an empty
  // shell — layout (Masthead etc.) is rendered by the route wrapper.
  if (unauthorized) return <></>;

  return (
    <React.Fragment>
      <Breadcrumbs
        items={[
          { title: "sla-monitor", link: "/sla-monitor" },
          { title: propertyName || "Property SLA" },
        ]}
      />
      <BackToPortfolioLink />
      <Header title={propertyName || "Loading…"} stamp="sla-monitor" />
      <Workspace id="sla-monitor-property" className="list">
        {/* Filters */}
        <div className="flex items-center flex-wrap gap-2 mb-5">
          <DateRangeFilter
            preset={filters.dateRangePreset}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onPresetChange={handlePresetChange}
            onCustomRange={handleCustomRange}
          />
          <TeamMemberFilter
            selectedIds={filters.teamMemberIds}
            onChange={handleTeamMembersChange}
            filters={filters}
            propertyId={propertyId}
          />
          <TaskLifecycleFilter
            value={filters.taskLifecycleStatus}
            onChange={handleLifecycleChange}
          />
        </div>

        {/* KPI metric cards — property-scoped summary */}
        <SlaMetricCards
          summary={isLoading ? null : (summary?.summary ?? null)}
          lifecycle={filters.taskLifecycleStatus}
        />

        {/* Property-scoped task list — per-section status filters managed inside */}
        <TasksRequiringAttention
          filters={filters}
          propertyId={propertyId}
        />
      </Workspace>
    </React.Fragment>
  );
};

export default memo(SlaMonitorProperty);
